import { useEffect, useMemo, useState } from 'react';
import { chargeMappingService } from '../services/chargeMappingService';
import { chargeMapService } from '../services/chargeMapService';
import { buildMapContext } from '../lib/comparisonSections';
import type { ChargeMapRow } from '../utils/chargeMap';
import type { NameAliases } from '../utils/nameAliases';
import type { MapContext } from '../utils/comparisonRows';
import type { ActualBillResult, BvBill } from '../types/billTypes';

type ChargeMapContextState = {
  mapContext: MapContext | undefined;
  /** False until the retailer's charge map has been looked up. A report must
   *  not be captured before then: its sections would be paired without the map. */
  ready: boolean;
};

/**
 * The charge map context for a bill, loaded the way the Comparison table loads
 * it: the retailer name (the site's, else the one parsed off the invoice)
 * resolved to a retailer id, then that retailer's map rows.
 *
 * A retailer that cannot be resolved, or a failed load, ends `ready` with no
 * map, which is exactly what the Comparison table shows in the same case:
 * pairing on canonical name alone.
 */
export const useChargeMapContext = (
  bill: BvBill,
  ar: ActualBillResult | null,
): ChargeMapContextState => {
  const retailerName = bill.site?.current_retailer || ar?.extracted_retailer || null;
  const [rows, setRows] = useState<ChargeMapRow[]>([]);
  const [aliases, setAliases] = useState<NameAliases | undefined>(undefined);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let mounted = true;
    setReady(false);
    setRows([]);

    const load = async () => {
      try {
        const res = await chargeMappingService.resolve(retailerName);
        const retailerIds = res.success ? res.data?.group?.map((r) => r.id) ?? [] : [];
        if (retailerIds.length === 0) return;
        const [mapRes, distAliases] = await Promise.all([
          chargeMapService.forRetailer(retailerIds),
          chargeMapService.distributorAliases(),
        ]);
        if (mounted) setAliases(distAliases);
        if (mounted && mapRes.success) setRows(mapRes.data ?? []);
      } catch (err) {
        console.error('Charge map load failed:', err);
      } finally {
        if (mounted) setReady(true);
      }
    };

    void load();
    return () => { mounted = false; };
  }, [retailerName]);

  const mapContext = useMemo(
    () => buildMapContext(rows, bill.site?.bv_extn, ar?.lines ?? [], aliases),
    [rows, bill.site, ar, aliases],
  );

  return { mapContext, ready };
};
